<template>
  <v-stepper-content step="4">
    <validation-observer v-slot="{ invalid }">
      <v-card class="mx-1 mb-12">
        <v-card-title>
          <div class="mr-4">{{ $t("form.labels.invoice") }}</div>
          <v-btn
            small
            color="primary"
            @click="transferUserData"
            v-if="currentUser"
          >
            <v-icon small left>mdi-arrow-down</v-icon>
            {{
              $t("form.btn.transferData", { username: currentUser.username })
            }}
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col>
              <v-text-field
                :label="$t('form.fields.firstname')"
                outlined
                v-model="invoice.firstname"
              ></v-text-field>
            </v-col>
            <v-col>
              <v-text-field
                :label="$t('form.fields.lastname')"
                outlined
                v-model="invoice.lastname"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="mt-n4">
            <v-col>
              <v-text-field
                :label="$t('form.fields.company')"
                outlined
                v-model="invoice.name"
              ></v-text-field>
            </v-col>
            <v-col>
              <v-text-field
                :label="$t('form.fields.department')"
                outlined
                v-model="invoice.department"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="mt-n4">
            <v-col>
              <v-text-field
                :label="$t('form.fields.companyAddition')"
                outlined
                v-model="invoice.companyAddition"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="mt-n4">
            <v-col>
              <validation-provider
                :name="$t('form.fields.street')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  :label="$t('form.fields.street') + ' *'"
                  outlined
                  v-model="invoice.street"
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="2">
              <validation-provider
                :name="$t('form.fields.zip')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  :label="$t('form.fields.zip') + ' *'"
                  outlined
                  v-model="invoice.zip"
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="3">
              <validation-provider
                :name="$t('form.fields.city')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  :label="$t('form.fields.city') + ' *'"
                  outlined
                  v-model="invoice.city"
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="mt-n4">
              <validation-provider
                :name="$t('form.fields.country')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-autocomplete
                  :label="$t('form.fields.country') + ' *'"
                  outlined
                  :items="countries"
                  item-text="value"
                  item-value="key"
                  :error-messages="errors"
                  :value="invoice.country"
                  @change="updateCountry"
                >
                </v-autocomplete>
              </validation-provider>
            </v-col>
          </v-row>
          <v-row class="mt-n4">
            <v-col>
              <v-text-field
                :label="$t('form.fields.phone')"
                outlined
                v-model="invoice.phone"
                type="tel"
              ></v-text-field>
            </v-col>
          </v-row>
          <validation-observer slim>
            <v-row class="mt-n4">
              <v-col>
                <validation-provider
                  :name="$t('form.fields.email')"
                  v-slot="{ errors }"
                  vid="original"
                  ref="emailField"
                  mode="eager"
                  :rules="
                    'required|email' + invoice.email === contact.email
                      ? '|confirmed:@confirm'
                      : ''
                  "
                >
                  <v-text-field
                    :label="$t('form.fields.email') + ' *'"
                    outlined
                    v-model="invoice.email"
                    :error-messages="errors"
                    type="email"
                  ></v-text-field>
                </validation-provider>
              </v-col>
              <v-col v-if="invoice.email !== contact.email">
                <validation-provider
                  :name="$t('form.fields.emailConfirmation')"
                  vid="confirm"
                  v-slot="{ errors }"
                  rules="required|email|confirmed:@original"
                >
                  <v-text-field
                    :label="$t('form.fields.emailConfirmation') + ' *'"
                    outlined
                    v-model="emailValidation"
                    @blur="validateEmailAddresses"
                    :error-messages="errors"
                    type="email"
                  ></v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
          </validation-observer>
          <v-row class="mt-n4">
            <v-col>
              <v-text-field
                :label="$t('form.fields.vatId')"
                outlined
                v-model="invoice.vatId"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row class="mt-n4" dense>
            <v-col cols="12">{{ $t("form.fields.combinedInvoice") }}</v-col>
            <v-col class="mt-n4">
              <v-radio-group
                :value="invoice.combinedInvoice"
                row
                mandatory
                dense
                @change="setCombinedInvoice({ value: $event })"
              >
                <v-radio :label="$t('yes')" :value="true"></v-radio>
                <v-radio :label="$t('no')" :value="false"></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>

          <v-row class="mt-n4">
            <v-col>
              <v-textarea
                :label="$t('form.fields.notes')"
                outlined
                v-model="invoice.notes"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <v-layout>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          class="mr-4"
          @click="$emit('onContinue')"
          :disabled="invalid"
        >
          {{ $t("form.btn.continue") }}
        </v-btn>
        <v-btn text @click="$emit('onBack')">{{ $t("form.btn.back") }}</v-btn>
      </v-layout>
    </validation-observer>
  </v-stepper-content>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import de from "../../i18n/de.json";

export default {
  props: ["multiple"],
  data() {
    return {
      emailValidation: null,
    };
  },
  methods: {
    ...mapMutations(["setCombinedInvoice", "updateCountry"]),
    transferUserData() {
      this.invoice.salutation = this.currentUser.attributes.salutation;
      this.invoice.firstname = this.currentUser.firstName;
      this.invoice.lastname = this.currentUser.lastName;
      this.invoice.title = this.currentUser.attributes.title;
      this.invoice.country =
        this.currentUser.attributes.invoiceCountry ||
        this.currentUser.attributes.country;
      if (this.currentUser.attributes.hasCompanyAddress) {
        this.invoice.name = this.currentUser.attributes.company;
        this.invoice.department = this.currentUser.attributes.department;
        this.invoice.companyAddition = this.currentUser.attributes.companyAddition;
      }
      this.invoice.street = this.currentUser.attributes.hasInvoiceAddress
        ? this.currentUser.attributes.invoiceStreet
        : this.currentUser.attributes.street;
      this.invoice.zip = this.currentUser.attributes.hasInvoiceAddress
        ? this.currentUser.attributes.invoiceZip
        : this.currentUser.attributes.zip;
      this.invoice.city = this.currentUser.attributes.hasInvoiceAddress
        ? this.currentUser.attributes.invoiceCity
        : this.currentUser.attributes.city;
      this.invoice.phone = this.currentUser.attributes.telephone;
      this.invoice.email = this.currentUser.email;
      this.emailValidation = this.currentUser.email;
      this.$nextTick(() => {
        this.validateEmailAddresses();
      });
    },
    validateEmailAddresses() {
      this.$refs.emailField.validate();
    },
  },
  computed: {
    ...mapGetters(["currentUser", "invoice", "contact"]),
    countries() {
      return Object.keys(de.countries).map((countryKey) => {
        return {
          key: countryKey,
          value: this.$t(`countries.${countryKey}`),
        };
      });
    },
  },
};
</script>

<style></style>
